// TimelapseShareModal — タイムラプス X 共有の編集1画面 + 進捗 + 共有ステップ
// (T3 スライス、growth-timelapse-share-2026-07-02.md §3 / §5.2 / §5.3)。
//
// フロー: 入口ボタン (ModernCommandCluster「絵ログを共有」) → 本モーダル。
//   1. loading  : 絵ログ一覧 + セグメント DL (loadStrokeLogTimeline、worker 外)。
//                 トリムスライダーの範囲材料 = **系譜連結ドメインの仮想列**
//                 (`buildTimelapseSliderDomain`、§4.7 past-canvas-new-document
//                 -2026-07-05.md — restore を「接続」として系譜チェーンを連結し、
//                 飛ばされた中間文書・非復元 tail・系譜外の全消し以前は可動域外)。
//   2. edit     : §5.2 確定仕様の編集1画面。**既定は無編集で即共有できる**
//                 (開いたら既定値のまま「作成」を押せば動く) を崩さない:
//                   - 時間範囲: 2ハンドルレンジスライダー (ラベルはイベント ts 由来の
//                     日時表示。プレビューは作らない)
//                   - 長さ: プリセット 15/30/60秒 (既定30) + カスタム秒数。
//                     副モードとして倍率 (×2〜×32) 切替
//                   - 自動クロップ ON/OFF (既定 ON)
//   3. generating: T2 `generateTimelapseVideo` (worker 一体 replay+encode)。
//                 進捗バー (ratio 1本) + キャンセル (AbortSignal → worker terminate)。
//   4. share    : 共有経路分岐 (§3 経路 C):
//                   - `navigator.canShare({files})` 真 (主にスマホ) → 「共有」で
//                     `navigator.share` (OS シート → X アプリに動画添付)
//                   - 不可 (主に PC) → 「動画を保存」(DL) + 「X で投稿画面を開く」
//                     (intent URL、プリフィル = ハッシュタグ + トップ URL。
//                      部屋 URL は入れない = 非公開部屋の漏洩防止、§3 確定)
//                 警告: skippedImagepastes / gaps / skippedSegments (T1/T2 申し送り)。
//
// テスト戦略 (vitest node 環境、ConfirmDialogView と同流儀): 各ステップの View を
// props 完結の pure 関数コンポーネントとして export し、tree walking で検証する。
// 非同期 orchestration (load / generate / share) は外側の stateful コンポーネント
// が担い、判断ロジックは application/timelapseShare.ts の pure ヘルパに寄せる。
//
// 描画エンジン / timelapse 生成層 (T1/T2) は変更しない (= import して使うだけ)。
// protocol / server 無変更。Classic への露出は本スライス対象外 (docs 注記の扱い)。

import type { StrokeLogEvent } from '@oekaki/protocol';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import type { Transport } from '../application/ports/Transport.js';
import { requestStrokeLogTail } from '../application/strokeLogTailRequest.js';
import {
  buildTimelapseSeqRanges,
  buildTimelapseSliderDomain,
  buildXIntentPostUrl,
  clampTimelapseCustomSeconds,
  DEFAULT_TIMELAPSE_LENGTH_SELECTION,
  decideTimelapseSharePath,
  describeTimelapseShareFailure,
  formatTimelapseRangeLabel,
  type NavigatorShareLike,
  resolveTimelapseMode,
  TIMELAPSE_DURATION_PRESETS,
  TIMELAPSE_EMPTY_LOG_MESSAGE,
  TIMELAPSE_MULTIPLIER_OPTIONS,
  TIMELAPSE_SHARE_TEXT,
  type TimelapseLengthSelection,
  type TimelapseSharePath,
  type TimelapseSliderDomain,
  timelapseFileName,
  timelapseWarnings,
} from '../application/timelapseShare.js';
import {
  classifyWebShareSettlement,
  reportTimelapseShare,
} from '../application/timelapseShareAnalytics.js';
import { readEnv } from '../infrastructure/env.js';
import {
  type CropRect,
  computeChangedRegionBox,
  isFullCanvasRect,
} from '../infrastructure/timelapse/cropBox.js';
import type { TimelapseEncodeSummary } from '../infrastructure/timelapse/encodeJob.js';
import { generateTimelapseVideo } from '../infrastructure/timelapse/generateTimelapseVideo.js';
import { createImagepasteResolver } from '../infrastructure/timelapse/imagepasteResolve.js';
import { collectFinalDocumentRanges } from '../infrastructure/timelapse/lineageSeed.js';
import {
  buildLiveTailSegment,
  combineLiveTail,
  liveTailSinceSeq,
} from '../infrastructure/timelapse/liveTail.js';
import {
  renderTimelapsePreviewFrame,
  type TimelapsePreviewVariant,
} from '../infrastructure/timelapse/previewFrame.js';
import { type SeqRange, trimEventsToRange } from '../infrastructure/timelapse/segmentMerge.js';
import {
  loadStrokeLogTimeline,
  type SkippedSegment,
} from '../infrastructure/timelapse/strokeLogTimeline.js';
import {
  DEFAULT_TIMELAPSE_QUALITY,
  estimateTimelapseOutput,
  isHighQualitySameAsStandard,
  resolveTimelapseQuality,
  shouldHintLongGeneration,
  type TimelapseQualityPreset,
} from '../infrastructure/timelapse/timelapseQuality.js';
import type { ResolveImagepasteFn } from '../infrastructure/timelapse/timelapseReplay.js';
import {
  PHONE_BODY_EDGE_INSET_CLASS,
  type TimelapseModalChrome,
  timelapseFooterRowClass,
  timelapseModalChrome,
} from './modalChrome.js';

// ── step state (discriminated union) ────────────────────────────────────────

export type TimelapseShareStepState =
  | { readonly t: 'loading' }
  | { readonly t: 'empty' }
  | { readonly t: 'load-failed'; readonly message: string }
  | { readonly t: 'edit' }
  | { readonly t: 'generating'; readonly ratio: number }
  | {
      readonly t: 'share';
      readonly file: File;
      readonly summary: TimelapseEncodeSummary;
      readonly path: TimelapseSharePath;
      readonly warnings: readonly string[];
    }
  | { readonly t: 'generate-failed'; readonly message: string };

// ── 共通スタイル (ConfirmDialog / 既存モーダルの視覚言語を踏襲) ─────────────

const primaryButtonClass =
  'rounded bg-[var(--color-accent)] px-4 py-2 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-40';
const secondaryButtonClass =
  'rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-sm text-white hover:bg-[var(--color-bg)]';
const choiceButtonClass = (active: boolean): string =>
  `rounded border px-2.5 py-1.5 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
  }`;
const sectionLabelClass = 'text-[11px] font-bold text-[var(--color-text-muted)]';

// ── 品質プリセット選択 (§5.5) ───────────────────────────────────────────────
//
// fps/解像度の生スライダーは出さず 2 プリセットに畳む (§5.5)。ラベルは解像度の
// 生数字でなく**行き先** (X向け / YouTube・X Premium向け) で見せる = 認知負荷を
// 下げ「既定のまま即共有」を崩さない。内部の maxEdge/fps/maxSeconds は
// resolveTimelapseQuality が preset × プラットフォーム × キャンバスから解決する
// (本 UI は preset だけ持つ)。
//
// 尺 cap の行き先 (consult 2026-07-06): 標準 = X 無料枠 (140秒) でそのまま通る cap、
// 高画質 = 長尺 (YouTube・X Premium は最大4時間) 想定でメモリ予算まで許す。
// ラベルもこの棲み分けを反映 (標準「X向け」/ 高画質「YouTube・X Premium向け」)。

export interface TimelapseQualityChoice {
  readonly preset: TimelapseQualityPreset;
  readonly label: string;
}

export const TIMELAPSE_QUALITY_CHOICES: readonly TimelapseQualityChoice[] = [
  { preset: 'standard', label: '標準 (X向け)' },
  { preset: 'high', label: '高画質 (YouTube・X Premium向け)' },
];

// ── 出力寸法 + 推定サイズのライブ表示 (§5.5 A / C) ──────────────────────────
//
// 各プリセットの**実際の出力寸法**と**推定ファイルサイズ**を具体値で並べ、切替の
// 意味を伝える。ソース長辺 ≤ 1280 だと標準/高画質が同一寸法・同一サイズになる
// (computeOutputSize は never-upscale) ので、同値が出ること自体 + 高画質側の短い
// 注記 (C) でサイレント no-op を無くす。寸法・サイズは modal 側が
// estimateTimelapseOutput で算出して props で渡す (この View は pure・表示のみ)。

/** 1 プリセット分のライブ表示データ (出力寸法 + 推定サイズ + 標準同値フラグ)。 */
export interface TimelapseQualityInfo {
  readonly preset: TimelapseQualityPreset;
  readonly label: string;
  /** 実出力寸法 (px)。`estimateTimelapseOutput` = 本番 computeOutputSize と同一。 */
  readonly width: number;
  readonly height: number;
  /** 推定ファイルサイズ (bytes)。倍速モード等でサイズ確定不可なら null → 「—」。 */
  readonly estimatedBytes: number | null;
  /** 高画質が標準と同一寸法になる (§5.5 C の注記対象。standard 側は常に false)。 */
  readonly sameAsStandard: boolean;
}

/**
 * 推定バイト数を人間可読に整形 (share ステップの `videoDurationSeconds` 表示と同じ
 * 1桁 MB 基調)。1MB 未満は KB、それ以上は MB。表示側で「〜」「推定」を添える。
 */
export const formatEstimatedFileSize = (bytes: number): string =>
  bytes >= 1_000_000
    ? `${(bytes / 1_000_000).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1_000))} KB`;

/**
 * 標準プリセット選択時に長さ設定へ添える軽い注記 (consult 2026-07-06)。標準の尺
 * cap は X 無料枠 (140秒) で頭打ちにしてあるので、その理由と「長尺は高画質へ」の
 * 逃げ道を控えめに示す (毎回大きく出さず、standard 選択時だけ小さく)。
 */
export const TIMELAPSE_STANDARD_LENGTH_NOTE = 'X無料枠は140秒まで。長尺は高画質へ';

// ── スマホ (フォンシェル) 対応 ─────────────────────────────────────────────
//
// モーダルは元々 Modern デスクトップ幅前提 (`w-[420px]` 固定 + 縦スクロールのみ) で、
// 実機スマホで中身が実効幅を超えると横あふれ → `overflow-y-auto` は横スクロール不可 →
// 右端 (スライダー右端・ボタン) が操作不能になっていた (growth-timelapse-share §5.3.4
// 実機 fb)。スマホ (= isPhoneViewport、M1 modern-phone-shell と同一判定) では
// フルスクリーン・縦積み・ヘッダ/フッター常時表示に切り替える。デスクトップ Modern の
// 見た目・挙動は完全不変 (下記 chrome の非モバイル枝が従来クラスそのまま)。
//
// 2026-07-09 過去キャンバス一覧モーダル (Slice 2) が同じ枠を使うため、chrome/footer
// ヘルパは `presentation/modalChrome.ts` へ切り出した。ここでは後方互換のため
// import した上で同名 re-export し (既存 TimelapseShareModal.test.ts の import を
// 壊さない)、コンポーネント内でも従来どおり参照する。
export type { TimelapseModalChrome };
export { PHONE_BODY_EDGE_INSET_CLASS, timelapseFooterRowClass, timelapseModalChrome };

// ── 2ハンドルレンジスライダー (§5.2 時間範囲トリム) ─────────────────────────
//
// ネイティブ <input type="range"> を 2 本重ねる定番構成: トラックは
// pointer-events-none、つまみだけ pointer-events-auto (Tailwind arbitrary
// variant)。a11y は各 input の aria-label (開始位置/終了位置) が担う。
// 値域はイベント index (0..eventCount-1)。ハンドル交差は onChange 側で clamp。

const rangeInputClass =
  'pointer-events-none absolute inset-x-0 top-1/2 h-6 w-full -translate-y-1/2 appearance-none bg-transparent ' +
  '[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 ' +
  '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full ' +
  '[&::-webkit-slider-thumb]:bg-[var(--color-accent)] [&::-webkit-slider-thumb]:border-none ' +
  '[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 ' +
  '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[var(--color-accent)] [&::-moz-range-thumb]:border-none';

export interface TimelapseRangeSliderProps {
  /**
   * スライダー値域の上限 index (= 仮想列の末尾)。値域は 0..maxIdx — 可動域の
   * クランプは index の下限ではなく材料側 (系譜連結ドメインの仮想列、§4.7) が
   * 担うので、下限は常に 0。
   */
  readonly maxIdx: number;
  readonly startIdx: number;
  readonly endIdx: number;
  readonly onChange: (startIdx: number, endIdx: number) => void;
}

export const TimelapseRangeSlider = ({
  maxIdx,
  startIdx,
  endIdx,
  onChange,
}: TimelapseRangeSliderProps): React.ReactNode => {
  // 選択区間のハイライト (%)。maxIdx=0 (イベント1件) は全幅扱い。
  const leftPct = maxIdx > 0 ? (startIdx / maxIdx) * 100 : 0;
  const rightPct = maxIdx > 0 ? (endIdx / maxIdx) * 100 : 100;
  return (
    <div className="relative h-6">
      {/* トラック + 選択区間ハイライト (装飾、操作は input が担う)。 */}
      <div
        aria-hidden="true"
        className="-translate-y-1/2 absolute inset-x-0 top-1/2 h-1 rounded-full bg-[var(--color-border)]"
      />
      <div
        aria-hidden="true"
        data-testid="timelapse-range-highlight"
        className="-translate-y-1/2 absolute top-1/2 h-1 rounded-full bg-[var(--color-accent)]"
        style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
      />
      <input
        type="range"
        aria-label="開始位置"
        min={0}
        max={maxIdx}
        step={1}
        value={startIdx}
        onChange={(e) => {
          const v = Number(e.currentTarget.value);
          onChange(Math.min(v, endIdx), endIdx);
        }}
        className={rangeInputClass}
      />
      <input
        type="range"
        aria-label="終了位置"
        min={0}
        max={maxIdx}
        step={1}
        value={endIdx}
        onChange={(e) => {
          const v = Number(e.currentTarget.value);
          onChange(startIdx, Math.max(v, startIdx));
        }}
        className={rangeInputClass}
      />
    </div>
  );
};

// ── 範囲プレビュー (修正3、§5.2「範囲選択時にプレビューを見たい」) ────────────
//
// 始点 (= 動画 frame 0 相当) / 終点 (= 完成形、動画最終フレーム相当) を小さく
// 表示する。stateful (canvas ref + debounce + async replay) なので pure な
// TimelapseEditStep からは `previewSlot` prop 経由で切り離す (= node の
// tree-walk テストが hook 付きコンポーネントを直接呼ばずに済む。既存 test 方針
// を崩さない)。負荷対策は previewFrame 側 (§ module header) + ここでの debounce。
// 失敗は握りつぶす。
//
// fix/timelapse-background (2026-09-27) で `events`(全量ログ) + `seqRanges`
// (動画生成と同一の `buildTimelapseSeqRanges` 結果) + `variant` を渡す形に
// 変更した — 始点/終点それぞれで別の切り詰め範囲を組み立てていた旧実装は
// 「境界イベント直後の leading run (全消し直後の background-set 等) が
// 切り詰めの toSeq で遮られる」バグの元だった (previewFrame.ts 冒頭参照)。

const PREVIEW_DEBOUNCE_MS = 250;

interface TimelapsePreviewPaneProps {
  /** **全量ログ** (動画生成に渡すのと同じ列)。 */
  readonly events: readonly StrokeLogEvent[];
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** 現在選択中の範囲 (= `buildTimelapseSeqRanges(domain, startIdx, endIdx)`)。 */
  readonly seqRanges: readonly SeqRange[];
  /** 始点 (frame 0 相当) / 終点 (完成形) のどちらを合成するか。 */
  readonly variant: TimelapsePreviewVariant;
  readonly label: string;
  /** 自動クロップ矩形 (修正2)。null = 全体プレビュー。 */
  readonly sourceRect: CropRect | null;
  /** imagepaste resolver (vandalism-recovery-ui-2026-09-27.md)。未指定 = 貼り付け画像を no-op。 */
  readonly resolveImagepaste?: ResolveImagepasteFn;
}

export const TimelapsePreviewPane = ({
  events,
  canvasWidth,
  canvasHeight,
  seqRanges,
  variant,
  label,
  sourceRect,
  resolveImagepaste,
}: TimelapsePreviewPaneProps): React.ReactNode => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // プレビュー再生成中フラグ。範囲・トグル等プレビュー入力が変わった瞬間 (= effect
  // 再実行) に true にし、debounce 待ち + replay 生成の窓すべてをカバーする。新フレーム
  // が描けたら (finally) false。連続ドラッグ中は cleanup の abort で finally が
  // setLoading(false) を踏まないので出っぱなし = 更新中を出し続ける (§5.3.5/§5.3.6)。
  // 重い部屋 (2000²・大量イベント) で最大 ~3 秒かかる再生成を「更新中」として見せる。
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    // スライダー確定を debounce (連続ドラッグ中は replay を回さない)。
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const frame = await renderTimelapsePreviewFrame({
            events,
            canvas: { width: canvasWidth, height: canvasHeight },
            seqRanges,
            variant,
            // 自動クロップ (修正2)。null なら渡さず全体プレビュー。
            ...(sourceRect !== null ? { sourceRect } : {}),
            ...(resolveImagepaste !== undefined ? { resolveImagepaste } : {}),
            signal: ac.signal,
          });
          if (ac.signal.aborted || frame === null) return;
          const cv = canvasRef.current;
          const ctx = cv?.getContext('2d') ?? null;
          if (cv === null || ctx === null) return;
          // width 代入は canvas をクリアする → putImageData と連続実行し、旧像は
          // 新像が出る直前まで残す (生成中のちらつき防止)。
          cv.width = frame.width;
          cv.height = frame.height;
          ctx.putImageData(new ImageData(frame.rgba, frame.width, frame.height), 0, 0);
        } catch {
          // プレビュー失敗は無害に握りつぶす (本番の動画生成には影響させない)。
        } finally {
          if (!ac.signal.aborted) setLoading(false);
        }
      })();
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      ac.abort();
      clearTimeout(timer);
    };
  }, [events, canvasWidth, canvasHeight, seqRanges, variant, sourceRect, resolveImagepaste]);

  return (
    // min-w-0: flex item (flex-1) の min-width は既定 auto = 中身 (canvas の intrinsic
    // width) になり、狭い画面で 2 枚が縮まず横あふれの主因になる。min-w-0 で content
    // より小さく縮められるようにし、canvas 側の max-w-full が実効幅に収める。
    <figure className="flex min-w-0 flex-1 flex-col items-center gap-1">
      <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)]">
        {/* 再生成中は旧フレームを薄暗くして「更新中」を伝える (中央スピナーと二重で明示)。
            transition-opacity で薄→濃の戻りも滑らかに。 */}
        <canvas
          ref={canvasRef}
          className={`max-h-full max-w-full object-contain transition-opacity${
            loading ? ' opacity-40' : ''
          }`}
        />
        {/* スピナーオーバーレイ (アプリ既存の視覚言語 = App.tsx / ImagepasteOverlay と
            同じ animate-spin リングを流用)。role=status で a11y、円自体は装飾。 */}
        {loading && (
          <span
            role="status"
            aria-label="プレビューを更新中"
            data-testid="timelapse-preview-loading"
            className="absolute inset-0 flex items-center justify-center"
          >
            <span
              aria-hidden="true"
              className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
            />
          </span>
        )}
      </div>
      <figcaption className="text-[10px] text-[var(--color-text-muted)]">{label}</figcaption>
    </figure>
  );
};

export interface TimelapsePreviewProps {
  /** **全量ログ** (動画生成に渡すのと同じ列)。 */
  readonly events: readonly StrokeLogEvent[];
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /**
   * 現在選択中の範囲 (= `buildTimelapseSeqRanges(domain, startIdx, endIdx)`)。
   * 始点・終点の両プレビューが同じ範囲を使う (= 動画生成と同一入力) — 差は
   * `TimelapsePreviewPane` の `variant` (frame 0 相当 / 完成形) だけ。
   */
  readonly seqRanges: readonly SeqRange[];
  /**
   * 自動クロップ矩形 (修正2)。動画生成と同じ crop を両プレビューに適用する。
   * null = 全体プレビュー (autoCrop OFF / crop 不要 = 全面)。
   */
  readonly sourceRect: CropRect | null;
  /** imagepaste resolver (vandalism-recovery-ui-2026-09-27.md)。未指定 = 貼り付け画像を no-op。 */
  readonly resolveImagepaste?: ResolveImagepasteFn;
}

/** 選択範囲の始点 (frame 0 相当) / 終点 (完成形) 2枚のプレビュー (§5.2、修正3)。 */
export const TimelapsePreview = ({
  events,
  canvasWidth,
  canvasHeight,
  seqRanges,
  sourceRect,
  resolveImagepaste,
}: TimelapsePreviewProps): React.ReactNode => (
  <section className="flex flex-col gap-1.5">
    <span className={sectionLabelClass}>プレビュー</span>
    <div className="flex gap-2">
      <TimelapsePreviewPane
        events={events}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
        seqRanges={seqRanges}
        variant="start"
        label="始点"
        sourceRect={sourceRect}
        {...(resolveImagepaste !== undefined ? { resolveImagepaste } : {})}
      />
      <TimelapsePreviewPane
        events={events}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
        seqRanges={seqRanges}
        variant="end"
        label="終点 (完成形)"
        sourceRect={sourceRect}
        {...(resolveImagepaste !== undefined ? { resolveImagepaste } : {})}
      />
    </div>
  </section>
);

// ── edit ステップ (§5.2 編集1画面) ──────────────────────────────────────────

export interface TimelapseEditStepProps {
  /**
   * スライダー材料の件数 (= 系譜連結ドメインの仮想列長、§4.7)。可動域は
   * [0, eventCount - 1] — 対象の作品 (系譜チェーン) へのクランプは modal が
   * 渡す仮想列そのものが担う。
   */
  readonly eventCount: number;
  readonly startIdx: number;
  readonly endIdx: number;
  /** `formatTimelapseRangeLabel(events[startIdx].ts, events[endIdx].ts)`。 */
  readonly rangeLabel: string;
  readonly onRangeChange: (startIdx: number, endIdx: number) => void;
  /** 範囲プレビュー (始点/終点 2枚)。stateful なので slot 注入 (pure テスト維持)。 */
  readonly previewSlot?: React.ReactNode;
  readonly selection: TimelapseLengthSelection;
  readonly onSelectionChange: (next: TimelapseLengthSelection) => void;
  /** 品質プリセット (§5.5、既定 standard)。maxEdge/fps/maxSeconds はこれから解決。 */
  readonly qualityPreset: TimelapseQualityPreset;
  readonly onQualityChange: (preset: TimelapseQualityPreset) => void;
  /**
   * 各プリセットの出力寸法 + 推定サイズ (§5.5 A / C)。modal が現在の sourceRect
   * (プレビュー/生成と同一) × 尺から `estimateTimelapseOutput` で算出して渡す。
   * 未指定 (テスト等) は寸法/サイズ行を出さない (プリセットボタンのみ)。
   */
  readonly qualityInfos?: readonly TimelapseQualityInfo[];
  /**
   * このプリセット × プラットフォーム × キャンバスで安全に生成できる最大秒数
   * (`resolveTimelapseQuality(...).maxSeconds`)。カスタム秒数入力の上限 + 秒数
   * プリセットタブの無効化判定に使う。
   */
  readonly maxSeconds: number;
  /**
   * 選択中の目標秒数が長時間生成域 (§5.5、120秒超) にあるか。true で「生成に数分
   * かかります」の控えめヒントを出す。倍速モードは尺が確定しないので常に false。
   */
  readonly longGenerationHint: boolean;
  readonly autoCrop: boolean;
  readonly onAutoCropChange: (v: boolean) => void;
  readonly onCreate: () => void;
  readonly onClose: () => void;
  /** スマホ時に主ボタン行を画面下部へ常時固定する (§5.3.4 実機 fb)。既定 false = 従来。 */
  readonly mobile?: boolean;
}

export const TimelapseEditStep = ({
  eventCount,
  startIdx,
  endIdx,
  rangeLabel,
  onRangeChange,
  previewSlot,
  selection,
  onSelectionChange,
  qualityPreset,
  onQualityChange,
  qualityInfos,
  maxSeconds,
  longGenerationHint,
  autoCrop,
  onAutoCropChange,
  onCreate,
  onClose,
  mobile = false,
}: TimelapseEditStepProps): React.ReactNode => (
  <div className="flex flex-col gap-4">
    {/* 時間範囲 (§5.2 トリム)。ラベルはイベント ts 由来の日時。 */}
    <section className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className={sectionLabelClass}>時間範囲</span>
        <span className="select-none text-xs text-[var(--color-text)]">{rangeLabel}</span>
      </div>
      <TimelapseRangeSlider
        maxIdx={eventCount - 1}
        startIdx={startIdx}
        endIdx={endIdx}
        onChange={onRangeChange}
      />
    </section>

    {/* 範囲プレビュー (修正3、始点/終点 2枚)。stateful なので slot 注入。 */}
    {previewSlot}

    {/* 長さ (§5.2): 秒数指定 (既定) ⇄ 倍速 (副モード)。 */}
    <section className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className={sectionLabelClass}>長さ</span>
        {/* 指定方法トグル (秒数 ⇄ 倍速)。ボタン自体の aria-pressed が状態を伝えるので
            role="group" は付けない (biome useSemanticElements)。 */}
        <div className="flex gap-1">
          <button
            type="button"
            aria-pressed={selection.lengthMode === 'duration'}
            onClick={() => onSelectionChange({ ...selection, lengthMode: 'duration' })}
            className={choiceButtonClass(selection.lengthMode === 'duration')}
          >
            秒数
          </button>
          <button
            type="button"
            aria-pressed={selection.lengthMode === 'multiplier'}
            onClick={() => onSelectionChange({ ...selection, lengthMode: 'multiplier' })}
            className={choiceButtonClass(selection.lengthMode === 'multiplier')}
          >
            倍速
          </button>
        </div>
      </div>
      {selection.lengthMode === 'duration' ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {TIMELAPSE_DURATION_PRESETS.map((seconds) => (
            <button
              key={seconds}
              type="button"
              // 品質プリセットの尺 cap (§5.5) を超える秒数プリセットは選べない
              // (メモリ安全圏の外)。実運用では 15/30/60 は cap を割らないが、将来
              // cap が下がっても UI が破綻しないよう無効化しておく。
              disabled={seconds > maxSeconds}
              aria-pressed={selection.durationChoice === seconds}
              onClick={() => onSelectionChange({ ...selection, durationChoice: seconds })}
              className={choiceButtonClass(selection.durationChoice === seconds)}
            >
              {seconds}秒
            </button>
          ))}
          <button
            type="button"
            aria-pressed={selection.durationChoice === 'custom'}
            onClick={() => onSelectionChange({ ...selection, durationChoice: 'custom' })}
            className={choiceButtonClass(selection.durationChoice === 'custom')}
          >
            カスタム
          </button>
          {selection.durationChoice === 'custom' && (
            <label className="flex items-center gap-1 text-xs text-[var(--color-text)]">
              <input
                type="number"
                aria-label="カスタム秒数"
                min={1}
                // 上限は品質プリセット × プラットフォームで解決した cap (§5.5)。
                max={maxSeconds}
                value={selection.customSeconds}
                onChange={(e) =>
                  onSelectionChange({ ...selection, customSeconds: e.currentTarget.value })
                }
                className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-2 py-1 text-white"
              />
              {`秒 (1〜${maxSeconds})`}
            </label>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {TIMELAPSE_MULTIPLIER_OPTIONS.map((x) => (
            <button
              key={x}
              type="button"
              aria-pressed={selection.multiplierX === x}
              onClick={() => onSelectionChange({ ...selection, multiplierX: x })}
              className={choiceButtonClass(selection.multiplierX === x)}
            >
              ×{x}
            </button>
          ))}
          <span className="text-[11px] text-[var(--color-text-muted)]">
            実描画時間ベース (休止は最大2秒に短縮)
          </span>
        </div>
      )}
      {/* 長時間生成ヒント (§5.5): 目標尺が 120 秒超のとき控えめに添える
          (生成時間は尺に線形、モバイル CPU では数分になりうる)。 */}
      {longGenerationHint && (
        <p className="text-[11px] text-[var(--color-text-muted)]">長い動画は生成に数分かかります</p>
      )}
      {/* 標準プリセットの尺 cap = X 無料枠 140秒 の注記 (consult 2026-07-06)。
          standard 選択時のみ、他の推定メタと同じ控えめな視覚言語で添える。 */}
      {qualityPreset === 'standard' && (
        <p className="text-[11px] text-[var(--color-text-muted)]">
          {TIMELAPSE_STANDARD_LENGTH_NOTE}
        </p>
      )}
    </section>

    {/* 画質 (§5.5): 2 プリセット。行き先 (X向け / YouTube・X Premium向け) で見せつつ、各
        プリセットの実出力寸法 + 推定サイズを併記して切替の意味を具体値で伝える
        (§5.5 A)。ソース ≤ 1280 で両者同値になる場合は高画質側に注記 (§5.5 C)。 */}
    <section className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className={sectionLabelClass}>画質</span>
        {qualityInfos !== undefined && (
          <span className="text-[10px] text-[var(--color-text-muted)]">サイズは推定</span>
        )}
      </div>
      <div className="flex flex-wrap items-start gap-1.5">
        {TIMELAPSE_QUALITY_CHOICES.map((choice) => {
          const info = qualityInfos?.find((q) => q.preset === choice.preset);
          return (
            <div key={choice.preset} className="flex min-w-[7.5rem] flex-col gap-1">
              <button
                type="button"
                aria-pressed={qualityPreset === choice.preset}
                onClick={() => onQualityChange(choice.preset)}
                className={choiceButtonClass(qualityPreset === choice.preset)}
              >
                {choice.label}
              </button>
              {info !== undefined && (
                <div className="px-0.5 text-[10px] leading-tight text-[var(--color-text-muted)]">
                  <div>{`${info.width}×${info.height}`}</div>
                  <div>
                    {info.estimatedBytes !== null
                      ? `〜${formatEstimatedFileSize(info.estimatedBytes)}`
                      : 'サイズは尺で変動'}
                  </div>
                  {info.sameAsStandard && (
                    <div className="text-amber-400/80">この絵では標準と同じ画質</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>

    {/* 自動クロップ (§5.2、既定 ON)。 */}
    <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
      <input
        type="checkbox"
        checked={autoCrop}
        onChange={(e) => onAutoCropChange(e.currentTarget.checked)}
      />
      描いた範囲に自動で寄せる (自動クロップ)
    </label>

    <div className={timelapseFooterRowClass(mobile)}>
      <button type="button" onClick={onClose} className={secondaryButtonClass}>
        キャンセル
      </button>
      <button type="button" onClick={onCreate} className={primaryButtonClass}>
        作成
      </button>
    </div>
  </div>
);

// ── generating ステップ (進捗バー + キャンセル) ────────────────────────────

export interface TimelapseGeneratingStepProps {
  /** 0..1 (T2 onProgress の overall ratio 1本)。 */
  readonly ratio: number;
  readonly onCancel: () => void;
  /** スマホ時にキャンセル行を画面下部へ常時固定する。既定 false = 従来。 */
  readonly mobile?: boolean;
}

export const TimelapseGeneratingStep = ({
  ratio,
  onCancel,
  mobile = false,
}: TimelapseGeneratingStepProps): React.ReactNode => {
  const pct = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[var(--color-text)]">動画を作成しています…</p>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label="動画作成の進捗"
        className="h-2 overflow-hidden rounded-full bg-[var(--color-border)]"
      >
        <div className="h-full bg-[var(--color-accent)]" style={{ width: `${pct}%` }} />
      </div>
      <div className={timelapseFooterRowClass(mobile)}>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          キャンセル
        </button>
      </div>
    </div>
  );
};

// ── share ステップ (§3 経路 C の 2 分岐) ────────────────────────────────────

export interface TimelapseShareStepProps {
  readonly path: TimelapseSharePath;
  /** 例 `30秒 / 640×640 / 2.1 MB`。 */
  readonly summaryLine: string;
  readonly warnings: readonly string[];
  /** Web Share (`navigator.share`) 起動。path='web-share' のときのみ使う。 */
  readonly onShare: () => void;
  readonly onDownload: () => void;
  /** X intent URL (path='download-intent' のときのみ使う)。 */
  readonly intentUrl: string;
  readonly onClose: () => void;
  /** スマホ時に主ボタン行を画面下部へ常時固定する。既定 false = 従来。 */
  readonly mobile?: boolean;
}

export const TimelapseShareStep = ({
  path,
  summaryLine,
  warnings,
  onShare,
  onDownload,
  intentUrl,
  onClose,
  mobile = false,
}: TimelapseShareStepProps): React.ReactNode => (
  <div className="flex flex-col gap-4">
    <p className="text-sm text-[var(--color-text)]">動画ができました ({summaryLine})</p>
    {warnings.length > 0 && (
      <ul className="flex flex-col gap-1">
        {warnings.map((w) => (
          <li key={w} className="text-amber-400 text-xs">
            ⚠️ {w}
          </li>
        ))}
      </ul>
    )}
    {path === 'web-share' ? (
      // スマホ想定: OS 共有シート → X アプリに動画添付済みで投稿画面 (§3 経路 C)。
      // 保存は補助導線として常置 (共有シートが X 添付に失敗した時の逃げ道)。
      <div className={timelapseFooterRowClass(mobile)}>
        <button type="button" onClick={onDownload} className={secondaryButtonClass}>
          動画を保存
        </button>
        <button type="button" onClick={onShare} className={primaryButtonClass}>
          共有
        </button>
      </div>
    ) : (
      // PC 想定: DL + intent の 2 ステップ (§3)。intent はメディア添付不可なので
      // 保存した動画を投稿画面に手で添付してもらう。
      <div className="flex flex-col gap-2">
        <p className="text-[11px] text-[var(--color-text-muted)]">
          動画を保存してから、X の投稿画面に添付してください
        </p>
        <div className={timelapseFooterRowClass(mobile)}>
          <button type="button" onClick={onDownload} className={secondaryButtonClass}>
            動画を保存
          </button>
          <a
            href={intentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={primaryButtonClass}
          >
            X で投稿画面を開く
          </a>
        </div>
      </div>
    )}
    <div className="flex justify-end">
      <button type="button" onClick={onClose} className="text-[var(--color-text-muted)] text-xs">
        閉じる
      </button>
    </div>
  </div>
);

// ── メッセージのみのステップ (loading / empty / failed) ────────────────────

export interface TimelapseMessageStepProps {
  readonly message: string;
  /** generate-failed のみ「編集に戻る」を出す。 */
  readonly onBackToEdit?: () => void;
  readonly onClose: () => void;
  /** スマホ時に主ボタン行を画面下部へ常時固定する。既定 false = 従来。 */
  readonly mobile?: boolean;
}

export const TimelapseMessageStep = ({
  message,
  onBackToEdit,
  onClose,
  mobile = false,
}: TimelapseMessageStepProps): React.ReactNode => (
  <div className="flex flex-col gap-4">
    <p className="whitespace-pre-wrap text-sm text-[var(--color-text)]">{message}</p>
    <div className={timelapseFooterRowClass(mobile)}>
      {onBackToEdit !== undefined && (
        <button type="button" onClick={onBackToEdit} className={secondaryButtonClass}>
          編集に戻る
        </button>
      )}
      <button type="button" onClick={onClose} className={secondaryButtonClass}>
        閉じる
      </button>
    </div>
  </div>
);

// ── stateful モーダル本体 ───────────────────────────────────────────────────

export interface TimelapseShareModalProps {
  readonly roomId: string;
  /** ルームのキャンバス寸法 (= RoomState.canvas。replay の原寸 raster 割当に必須)。 */
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /**
   * 絵ログ live tail (feature/stroke-log-live-tail) の WS 往復に使う。開いた
   * 時点で 1 回 `whiteboard.stroke-log-tail-request` を撃ち、R2 に未 flush の
   * 最新描画 (直近最大5分) を取り込んで動画・プレビュー・トリム材料すべてを
   * 最新まで反映する。socket が閉じている / 応答が無い / 非在室のときは tail
   * 無し (R2 分のみ) で degrade する。
   */
  readonly transport: Transport;
  /**
   * `createTimelapseWorker` (worker/timelapseWorkerFactory) を呼び出し側
   * (ModernLayout) が注入する。`?worker` import をこのモジュールに持ち込まない
   * ことで node 環境の vitest から import 可能に保つ (= generateTimelapseVideo
   * が workerFactory を引数にしているのと同じ方針、T2 module header 参照)。
   */
  readonly workerFactory: () => Worker;
  /**
   * スマホ (= フォンシェル、isPhoneViewport: 幅 < 640 ‖ coarse pointer × 短辺 < 640)
   * か。ModernLayout の `useLayoutPreference.isMobile` をそのまま渡す (M1 と同一判定、
   * resize / matchMedia 追従で回転にも追随)。true でフルスクリーン・縦積み・
   * ヘッダ/フッター常時表示に切り替える (§5.3.4 実機 fb)。既定 false = デスクトップ。
   */
  readonly isMobile?: boolean;
  /**
   * 該当ルームの owner 用 admin_token (client 保持分、AdminTokenStore 由来)。絵ログ
   * listing (`GET /rooms/:roomId/stroke-log`) はオーナー限定にした (consult 2026-07-08
   * 案B) ので `Authorization: Bearer` として送る。絵ログボタン自体が owner 限定表示
   * なので通常は必ず入るが、防御的に optional (無ければ無認証で撃ち 401/403 → load-failed
   * に落ちてモーダルは固まらない)。
   */
  readonly adminToken?: string;
  /**
   * 過去キャンバスから開いたとき (past-canvas-new-document-2026-07-05.md §4.5-3、
   * Slice 2) の**可視範囲の終端 seq**。その過去キャンバスの最終 snapshot 時点の seq
   * (= `PastCanvasListItem.snapshotSeq`) を渡すと、スライダードメイン
   * (`buildTimelapseSliderDomain`) の終端をそこにした上で**その過去キャンバスの
   * 系譜**を可動域にする (§4.7 — 従来の「その文書のみ」から系譜込みに拡大)。
   * 省略時 (= 現在キャンバスから通常の絵ログ共有を開いた場合) は終端 = 最新
   * イベントで、今画面に残っている最終文書の系譜がドメインになる。
   */
  readonly initialEndSeq?: number;
  readonly onClose: () => void;
}

/** DL 起動 (devHarness と同型のローカルヘルパ。capture PNG の流儀)。 */
const triggerDownload = (file: File): void => {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.click();
  // click が URL を消費した後に revoke (次 tick)。
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
};

export const TimelapseShareModal = ({
  roomId,
  canvasWidth,
  canvasHeight,
  transport,
  workerFactory,
  isMobile = false,
  adminToken,
  initialEndSeq,
  onClose,
}: TimelapseShareModalProps): React.ReactNode => {
  const titleId = useId();
  const [step, setStep] = useState<TimelapseShareStepState>({ t: 'loading' });
  // 編集 state (edit ⇄ generating ⇄ share を往復しても保持)。R2 + live tail を
  // 結合済みの**全量**イベント列 (生成の preloadedTimeline / クロップ材料。系譜
  // seed の再構築材料が要るので可動域外も捨てない)。
  const [events, setEvents] = useState<readonly StrokeLogEvent[] | null>(null);
  // 系譜連結ドメイン (§4.7): スライダーが操作する仮想列 + その範囲リスト。
  // トリム材料 / 範囲ラベル / プレビュー / 生成 request の写像すべての基準。
  const [domain, setDomain] = useState<TimelapseSliderDomain | null>(null);
  // 生成 summary の警告に流す merge 診断 (gaps / skippedSegments)。生成のたびに
  // 再取得せず、mount 時に取り込んだ結合済み timeline のものを使い回す。
  const timelineExtrasRef = useRef<{
    gaps: readonly SeqRange[];
    skippedSegments: readonly SkippedSegment[];
  }>({ gaps: [], skippedSegments: [] });
  // トリム選択 (仮想列 index の 2 ハンドル)。
  const [range, setRange] = useState<readonly [number, number]>([0, 0]);
  const [selection, setSelection] = useState<TimelapseLengthSelection>(
    DEFAULT_TIMELAPSE_LENGTH_SELECTION,
  );
  const [autoCrop, setAutoCrop] = useState(true);
  // 品質プリセット (§5.5、既定 standard)。maxEdge/fps/maxSeconds はここから解決。
  const [qualityPreset, setQualityPreset] =
    useState<TimelapseQualityPreset>(DEFAULT_TIMELAPSE_QUALITY);
  // 生成キャンセル (worker terminate)。unmount 時も abort (= × / 退室で確実に止める)。
  const generationAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => generationAbortRef.current?.abort(), []);

  // プリセット × プラットフォーム (isMobile) × キャンバス寸法 → 生成パラメータ
  // (§5.5)。isMobile は Modern が渡す実値 (Classic は省略=false=デスクトップ扱い)。
  const resolvedQuality = useMemo(
    () => resolveTimelapseQuality(qualityPreset, { isMobile, canvasWidth, canvasHeight }),
    [qualityPreset, isMobile, canvasWidth, canvasHeight],
  );

  // プリセット変更: maxSeconds が下がって現在のカスタム秒数がそれを超える場合は
  // clamp する (§5.5)。それ以外は据え置き (下げても超えていなければ触らない)。
  const handleQualityChange = useCallback(
    (preset: TimelapseQualityPreset): void => {
      setQualityPreset(preset);
      const nextMax = resolveTimelapseQuality(preset, {
        isMobile,
        canvasWidth,
        canvasHeight,
      }).maxSeconds;
      setSelection((sel) => {
        const raw = Number(sel.customSeconds);
        return Number.isFinite(raw) && raw > nextMax
          ? { ...sel, customSeconds: `${clampTimelapseCustomSeconds(raw, nextMax)}` }
          : sel;
      });
    },
    [isMobile, canvasWidth, canvasHeight],
  );

  // mount 時: R2 絵ログ一覧 + セグメント DL → その最新 seq を sinceSeq に
  // `whiteboard.stroke-log-tail-request` を 1 回撃ち、未 flush の live tail を
  // 結合する (絵ログ live tail、feature/stroke-log-live-tail)。結合済み列を
  // トリム材料 / プレビュー / 生成すべてで使い回す。tail 取得失敗・空・非在室は
  // R2 分のみで続行 (機能を壊さない degrade)。
  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      const outcome = await loadStrokeLogTimeline({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        signal: ac.signal,
        // owner 認証 (consult 2026-07-08 案B): listing に admin_token を Bearer で送る。
        ...(adminToken !== undefined ? { adminToken } : {}),
      });
      if (ac.signal.aborted) return;
      if (outcome.outcome !== 'ok') {
        // LoadStrokeLogTimelineOutcome の 'error' は生成 outcome の 'load-error'
        // と同じユーザ向け意味 (取得失敗) なので文言 map を共有する。
        setStep({
          t: 'load-failed',
          message: describeTimelapseShareFailure(
            outcome.outcome === 'error' ? 'load-error' : outcome.outcome,
          ),
        });
        return;
      }
      // R2 の最新 seq 以降を WS で問い合わせ → 疑似セグメントに wrap → 結合。
      // ここで初めて「絵ログが空か」を確定する (R2 が空でも live tail に最新の
      // 描画があり得るため、tail 結合後に判定する)。
      const tail = await requestStrokeLogTail(
        transport,
        liveTailSinceSeq(outcome.timeline.coverage),
        { signal: ac.signal },
      );
      if (ac.signal.aborted) return;
      const combined = combineLiveTail(
        outcome.timeline,
        tail !== null ? buildLiveTailSegment(roomId, tail) : null,
      );
      const list = combined.events;
      if (list.length === 0) {
        setStep({ t: 'empty' });
        return;
      }
      timelineExtrasRef.current = {
        gaps: combined.gaps,
        skippedSegments: combined.skippedSegments,
      };
      // スライダー可動域 = 系譜連結ドメイン (§4.7)。restore は「壁」ではなく
      // 「接続」— 「続きを描く」直後 (続き未描画) でも復元元の系譜が可動域に
      // 入り、ドメインは空にならない。系譜外の全消し以前へのクランプ
      // (v0.2.2-beta1 の意図) は先頭範囲の下端 = 最終文書の開始として維持。
      // 過去キャンバスから開いた場合 (initialEndSeq = snapshotSeq、§4.5-3
      // Slice 2) は終端をその seq にした「その過去キャンバスの系譜」。
      // 全量イベント列 (list) は生成の preloadedTimeline / クロップ材料として
      // 別に保持する (仮想列だけでは系譜 seed の再構築材料が引けない)。
      const nextDomain = buildTimelapseSliderDomain(list, initialEndSeq);
      if (nextDomain.events.length === 0) {
        // list 非空ならここには来ない (buildTimelapseSliderDomain が最新終端に
        // 倒す) — 万一の防御で empty に落としてモーダルを固まらせない。
        setStep({ t: 'empty' });
        return;
      }
      setEvents(list);
      setDomain(nextDomain);
      // 既定選択: 開始 = 仮想列の先頭 (= 系譜 root の文書開始)、終端 = 末尾
      // (§4.7 決定 — 従来の「復元以降を既定開始」から変更。境界 (restore /
      // 系譜外の全消し) は仮想列に現れないので defaultTimelapseStartIndex は不要)。
      setRange([0, nextDomain.events.length - 1]);
      setStep({ t: 'edit' });
    })();
    return () => ac.abort();
  }, [roomId, transport, adminToken, initialEndSeq]);

  // 自動クロップ矩形 (修正2): 選択範囲のイベントから動画生成と同一ロジック
  // (`computeChangedRegionBox`) で crop を求め、プレビューにも反映する。全面矩形
  // (= crop 不要) と autoCrop OFF は null (= 全体プレビュー / crop 無し)。範囲・
  // トグルの変化に追従 (プレビューの debounce は Pane 側に乗る)。
  const previewCrop = useMemo<CropRect | null>(() => {
    if (!autoCrop || events === null || domain === null) return null;
    const last = domain.events[range[1]];
    if (last === undefined) return null;
    // 土台 seed (§5 + 系譜 seed §4.5-2) を含めた材料でクロップを求め、動画生成
    // (encodeJob) と一致させる。材料 = 「選択終端時点で画面に残る最終文書」を
    // 構成する系譜範囲 (encodeJob と同じ collectFinalDocumentRanges を全量列に
    // 当てる)。始点プレビューは replay が系譜 root から回るので土台込みで映る
    // が、クロップが可視範囲だけだと土台がはみ出す分が切れてしまう。restore の
    // 無いログでは従来の「最後の全消し〜終端」単一範囲と同じ box になる。
    const trimmed = collectFinalDocumentRanges(events, last.seq).flatMap((r) =>
      trimEventsToRange(events, r),
    );
    if (trimmed.length === 0) return null;
    const canvas = { width: canvasWidth, height: canvasHeight };
    const box = computeChangedRegionBox(trimmed, canvas);
    return isFullCanvasRect(box, canvas) ? null : box;
  }, [autoCrop, events, domain, range, canvasWidth, canvasHeight]);

  // プレビューの範囲 (fix/timelapse-background 2026-09-27 再修正)。
  // **動画生成と同一の入力** (`events` = 全量ログ、`buildTimelapseSeqRanges` の
  // 結果) を両プレビュー (始点・終点) にそのまま渡す — モーダル側で index ごと
  // に別の切り詰め範囲や継承背景色 (`backgroundAt`) を個別に組み立てない
  // (二重実装の回避)。leading-run/背景色の継承は `createTimelapseFrameSource`
  // 内部の既定計算 (`previewFrame.ts` が渡す `events`=全量ログ) にそのまま
  // 委ねる — こうすることで、境界イベント直後の leading run (全消し直後の
  // background-set 等) が「その時点までの切り詰め」の toSeq で遮られて土台に
  // 取り込まれない、という過去の再発 (始点プレビューだけ色がずれた) を防ぐ
  // (previewFrame.ts 冒頭コメント参照)。
  const previewSeqRanges = useMemo<readonly SeqRange[] | null>(() => {
    if (events === null || domain === null) return null;
    return buildTimelapseSeqRanges(domain, range[0], range[1]);
  }, [events, domain, range]);

  // imagepaste resolver (vandalism-recovery-ui-2026-09-27.md「タイムラプスの
  // imagepaste 再生」節) — 編集1画面のプレビュー (main thread) 用。domain が
  // 定まるたびに 1 個作り、以降のスライダー操作 (debounce ごとの
  // renderTimelapsePreviewFrame 呼び出し) はこの単一インスタンスを再利用する
  // (= 再署名・prefetch は domain 確定時に 1 回だけ)。生成 (worker 側) は
  // 別インスタンスを持つ (module header「片方だけだとずれる」は resolve の
  // 挙動の話であって、プロセス/インスタンスの共有までは要求していない —
  // 同じ basename は各インスタンスの Blob cache がそれぞれ吸収する)。
  const previewImagepasteResolver = useMemo(() => {
    if (domain === null) return undefined;
    return createImagepasteResolver({
      dispatcherUrl: readEnv().dispatcherUrl,
      roomId,
      ...(adminToken !== undefined ? { adminToken } : {}),
      events: domain.events,
    });
  }, [domain, roomId, adminToken]);

  // 各プリセットの出力寸法 + 推定サイズ (§5.5 A / C)。ソースは**プレビュー/生成と
  // 同一の sourceRect** (自動クロップ ON = previewCrop、OFF/全面 = キャンバス全体) を
  // 再利用して推定と本番出力がズレないようにする。尺は duration モードのみ確定
  // (倍速は出力長が未定 → estimatedBytes は null で「サイズは尺で変動」に degrade)。
  const qualityInfos = useMemo<readonly TimelapseQualityInfo[]>(() => {
    const source =
      previewCrop !== null
        ? { width: previewCrop.width, height: previewCrop.height }
        : { width: canvasWidth, height: canvasHeight };
    const mode = resolveTimelapseMode(selection, resolvedQuality.maxSeconds);
    const durationSeconds = mode.kind === 'duration' ? mode.seconds : undefined;
    const redundant = isHighQualitySameAsStandard(source.width, source.height);
    return TIMELAPSE_QUALITY_CHOICES.map((choice) => {
      const est = estimateTimelapseOutput({
        preset: choice.preset,
        sourceWidth: source.width,
        sourceHeight: source.height,
        ...(durationSeconds !== undefined ? { durationSeconds } : {}),
      });
      return {
        preset: choice.preset,
        label: choice.label,
        width: est.width,
        height: est.height,
        estimatedBytes: est.estimatedBytes,
        sameAsStandard: choice.preset === 'high' && redundant,
      };
    });
  }, [previewCrop, canvasWidth, canvasHeight, selection, resolvedQuality.maxSeconds]);

  const handleCreate = useCallback((): void => {
    if (events === null || domain === null) return;
    const ac = new AbortController();
    generationAbortRef.current = ac;
    setStep({ t: 'generating', ratio: 0 });
    void (async () => {
      const outcome = await generateTimelapseVideo({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        canvas: { width: canvasWidth, height: canvasHeight },
        // トリム選択 (仮想列 index) → 系譜連結ドメインの範囲リストへ写像
        // (§4.7)。選択が範囲間ジャンプ (restore の接続) を跨げば複数範囲に
        // 分割され、範囲外 (中間文書 B / 非復元 tail / マーカー) は生成にも
        // 現れない。events は全量列を渡す (replay の系譜 seed 材料)。
        seqRanges: buildTimelapseSeqRanges(domain, range[0], range[1]),
        // §5.5: 品質プリセット × プラットフォームで解決した maxSeconds を渡し、
        // カスタム秒数をメモリ安全な cap に clamp する。maxEdge/fps も明示指定。
        mode: resolveTimelapseMode(selection, resolvedQuality.maxSeconds),
        maxEdge: resolvedQuality.maxEdge,
        fps: resolvedQuality.fps,
        maxSeconds: resolvedQuality.maxSeconds,
        autoCrop,
        // imagepaste 再署名 (vandalism-recovery-ui-2026-09-27.md): worker 側
        // (encodeJob.ts) が admin_token 付きで `imagepaste-urls` を叩ける
        // ようにする。非 owner (未指定) は worker が元 URL のみで試す。
        ...(adminToken !== undefined ? { adminToken } : {}),
        // 絵ログ live tail: mount 時に結合済みのイベント列を worker にそのまま
        // 渡す (worker からは WS が届かないので main で取得済みのものを使う)。
        preloadedTimeline: {
          events,
          gaps: timelineExtrasRef.current.gaps,
          skippedSegments: timelineExtrasRef.current.skippedSegments,
        },
        workerFactory,
        onProgress: (p) => {
          setStep((prev) => (prev.t === 'generating' ? { t: 'generating', ratio: p.ratio } : prev));
        },
        signal: ac.signal,
      });
      generationAbortRef.current = null;
      switch (outcome.outcome) {
        case 'ok': {
          const file = new File([outcome.blob], timelapseFileName(new Date()), {
            type: 'video/mp4',
          });
          // §5.4 生成数: 動画生成が成功した時点で 1 件計上 (fire-and-forget)。
          reportTimelapseShare(transport, 'generate');
          setStep({
            t: 'share',
            file,
            summary: outcome.summary,
            path: decideTimelapseSharePath(navigator as NavigatorShareLike, file),
            warnings: timelapseWarnings(outcome.summary),
          });
          return;
        }
        case 'cancelled':
          setStep({ t: 'edit' });
          return;
        case 'empty-log':
          setStep({ t: 'empty' });
          return;
        default:
          setStep({
            t: 'generate-failed',
            message: describeTimelapseShareFailure(outcome.outcome),
          });
      }
    })();
  }, [
    events,
    domain,
    range,
    selection,
    resolvedQuality,
    autoCrop,
    roomId,
    canvasWidth,
    canvasHeight,
    workerFactory,
    transport,
    adminToken,
  ]);

  const handleCancelGeneration = useCallback((): void => {
    generationAbortRef.current?.abort();
    generationAbortRef.current = null;
    // abort → outcome 'cancelled' → edit へ戻る (handleCreate 側)。ここでは何もしない
    // が、progress 更新レースで generating 表示が残らないよう即 edit へ倒す。
    setStep({ t: 'edit' });
  }, []);

  const settleWebShare = useCallback(
    (error: unknown): void => {
      switch (classifyWebShareSettlement(error)) {
        case 'shared':
          // §5.4 共有完了数: Web Share が resolve = OS シートで共有先に渡った時点で計上。
          reportTimelapseShare(transport, 'share');
          return;
        case 'aborted':
          // ユーザが共有シートを閉じた (正常系)。§5.3.4 の黙殺方針どおり計上しない。
          return;
        case 'failed':
          // X アプリ側の受け取り失敗等 → DL + intent へフォールバック (計上はそちらで)。
          setStep((prev) => (prev.t === 'share' ? { ...prev, path: 'download-intent' } : prev));
      }
    },
    [transport],
  );

  const handleShare = useCallback((): void => {
    if (step.t !== 'share') return;
    const nav = navigator as NavigatorShareLike;
    void nav.share?.({ files: [step.file], text: TIMELAPSE_SHARE_TEXT }).then(
      () => settleWebShare(null),
      (e: unknown) => settleWebShare(e),
    );
  }, [step, settleWebShare]);

  const handleDownload = useCallback((): void => {
    if (step.t !== 'share') return;
    // §5.4 共有完了数: PC 導線は「動画を保存 → X で投稿」。保存 (DL) を開始した時点を
    // 共有成立とみなして計上する (intent リンクは別ウィンドウ遷移で捕捉が不確実な
    // ため、確実に取れる保存アクションをシグナルにする)。
    reportTimelapseShare(transport, 'share');
    triggerDownload(step.file);
  }, [step, transport]);

  // 生成中はうっかり閉じ (backdrop / Esc) を無効化 (= 分単位のエンコードを誤タップで
  // 失わない。閉じたければ「キャンセル」→ edit → キャンセル)。share ステップも同様に
  // backdrop では閉じない (= できた動画を誤タップで捨てない。「閉じる」ボタンで明示)。
  const lightDismiss = step.t === 'loading' || step.t === 'edit' || step.t === 'empty';
  useEffect(() => {
    if (!lightDismiss) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightDismiss, onClose]);

  const body = ((): React.ReactNode => {
    switch (step.t) {
      case 'loading':
        return (
          <p role="status" className="text-sm text-[var(--color-text-muted)]">
            絵ログを読み込んでいます…
          </p>
        );
      case 'empty':
        return (
          <TimelapseMessageStep
            message={TIMELAPSE_EMPTY_LOG_MESSAGE}
            onClose={onClose}
            mobile={isMobile}
          />
        );
      case 'load-failed':
        return <TimelapseMessageStep message={step.message} onClose={onClose} mobile={isMobile} />;
      case 'edit': {
        // 不変条件: edit はドメイン構築済のみ (`events`/`previewSeqRanges` も同時に
        // 確定済み — loading エフェクトが 3 つを同時に set する)。
        if (domain === null || events === null || previewSeqRanges === null) return null;
        // スライダー / ラベル / プレビューの材料はすべて仮想列 (§4.7)。プレビュー
        // の prefix replay も仮想列上で回す — 範囲外 (中間文書 / restore マーカー)
        // が列に無いので、系譜チェーンの「重ねて適用」意味論 (lineageSeed.ts) で
        // その時点の合成 = 生成される動画のフレームと一致する。
        const first = domain.events[range[0]];
        const last = domain.events[range[1]];
        return (
          <TimelapseEditStep
            eventCount={domain.events.length}
            startIdx={range[0]}
            endIdx={range[1]}
            rangeLabel={
              first !== undefined && last !== undefined
                ? formatTimelapseRangeLabel(first.ts, last.ts)
                : ''
            }
            onRangeChange={(s, e) => setRange([s, e])}
            previewSlot={
              <TimelapsePreview
                events={events}
                canvasWidth={canvasWidth}
                canvasHeight={canvasHeight}
                seqRanges={previewSeqRanges}
                sourceRect={previewCrop}
                {...(previewImagepasteResolver !== undefined
                  ? { resolveImagepaste: previewImagepasteResolver.resolve }
                  : {})}
              />
            }
            selection={selection}
            onSelectionChange={setSelection}
            qualityPreset={qualityPreset}
            onQualityChange={handleQualityChange}
            qualityInfos={qualityInfos}
            maxSeconds={resolvedQuality.maxSeconds}
            longGenerationHint={((): boolean => {
              // 目標尺の長時間ヒント (§5.5)。倍速モードは尺が確定しないので false。
              const mode = resolveTimelapseMode(selection, resolvedQuality.maxSeconds);
              return mode.kind === 'duration' && shouldHintLongGeneration(mode.seconds);
            })()}
            autoCrop={autoCrop}
            onAutoCropChange={setAutoCrop}
            onCreate={handleCreate}
            onClose={onClose}
            mobile={isMobile}
          />
        );
      }
      case 'generating':
        return (
          <TimelapseGeneratingStep
            ratio={step.ratio}
            onCancel={handleCancelGeneration}
            mobile={isMobile}
          />
        );
      case 'share':
        return (
          <TimelapseShareStep
            path={step.path}
            summaryLine={`${Math.round(step.summary.videoDurationSeconds)}秒 / ${step.summary.width}×${step.summary.height} / ${(step.file.size / 1_000_000).toFixed(1)} MB`}
            warnings={step.warnings}
            onShare={handleShare}
            onDownload={handleDownload}
            intentUrl={buildXIntentPostUrl()}
            onClose={onClose}
            mobile={isMobile}
          />
        );
      case 'generate-failed':
        return (
          <TimelapseMessageStep
            message={step.message}
            onBackToEdit={() => setStep({ t: 'edit' })}
            onClose={onClose}
            mobile={isMobile}
          />
        );
    }
  })();

  const chrome = timelapseModalChrome(isMobile);

  return (
    <div className={chrome.root}>
      {/* backdrop: lightDismiss ステップのみクリックで閉じる (生成中/共有ステップは
          明示ボタンのみ = 誤タップでエンコード結果を捨てない)。スマホはフルスクリーン
          で dialog が画面を覆う (= backdrop の下地が見えず、閉じは常時可視の × のみ)
          ため backdrop 自体を出さない。 */}
      {!isMobile &&
        (lightDismiss ? (
          <button
            type="button"
            aria-label="閉じる"
            tabIndex={-1}
            onClick={onClose}
            className="-z-10 absolute inset-0 cursor-default bg-black/60"
          />
        ) : (
          <div aria-hidden="true" className="-z-10 absolute inset-0 bg-black/60" />
        ))}
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className={chrome.dialog}>
        <div className={chrome.header}>
          <h2 id={titleId} className="font-bold text-[var(--color-accent)] text-base">
            絵ログを共有
          </h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]"
          >
            ×
          </button>
        </div>
        <div className={chrome.body}>{body}</div>
      </div>
    </div>
  );
};
